import { Injectable } from '@angular/core';
// FIX: Corrected import path for models.
import { Ticket, Order, AppUser, OrderItem, LoyaltySettings } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

const ORDER_TABLE = 'orders';
const ORDER_ITEM_TABLE = 'order_items';

@Injectable({ providedIn: 'root' })
export class OrderService {
  private supabase: SupabaseClient | null = getSupabase();

  async getOrders(): Promise<Order[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
      // No mock data for this, as it's a back-office feature that depends on real data.
      return Promise.resolve([]);
    }
    
    // RLS will handle filtering by store_id automatically.
    const { data, error } = await this.supabase
      .from(ORDER_TABLE)
      .select(`
        id,
        order_number,
        total_amount,
        createdAt,
        items:order_items(id, name:products(name), quantity, price_per_unit, subtotal, productId:product_id),
        customers ( full_name ),
        cashier:users!cashier_id ( name )
      `)
      .eq('status', 'COMPLETED')
      .order('createdAt', { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error fetching orders for receipt list:", error.message);
      throw error;
    }
    
    return (data || []).map((o: any) => ({
      id: o.id,
      orderNumber: o.order_number,
      totalAmount: o.total_amount,
      createdAt: o.createdAt,
      customerName: o.customers?.full_name,
      cashierName: o.cashier?.name,
      items: o.items.map((i: any) => ({
        id: i.id,
        name: i.name.name,
        quantity: i.quantity,
        pricePerUnit: i.price_per_unit,
        subtotal: i.subtotal,
        productId: i.productId
      }))
    })) as Order[];
  }
  
  async findOrderByNumber(orderNumber: string): Promise<Order | null> {
    if (!isSupabaseConfigured() || !this.supabase) return null;

    const { data, error } = await this.supabase
      .rpc('find_returnable_order_by_number', { p_order_number: orderNumber });

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      orderNumber: data.order_number,
      totalAmount: data.total_amount,
      createdAt: data.createdAt,
      customerName: data.customer_name,
      cashierName: data.cashier_name,
      customerId: data.customer_id,
      pointsEarned: data.points_earned,
      items: data.items.map((i: any) => ({
        id: i.id,
        name: i.name,
        quantity: i.quantity,
        pricePerUnit: i.price_per_unit,
        subtotal: i.subtotal,
        productId: i.product_id
      }))
    } as Order;
  }

  async processOrder(
    ticket: Ticket,
    paymentMethod: string,
    total: number,
    discountAmount: number,
    shiftId: string,
    cashier: AppUser,
    loyaltySettings: LoyaltySettings | null
  ): Promise<Order> {

    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve({ id: `mock-${Date.now()}`, orderNumber: 'MOCK-001', totalAmount: total, shiftId: 'mock-shift' } as Order);
    }
    const db = this.supabase;

    // 1. Create Order
    const pointsPerBaht = loyaltySettings?.pointsPerBaht ?? 0.01; // Default to 1 per 100 if not set
    const pointsEarned = Math.floor(total * pointsPerBaht);
    const orderPayload: any = {
      order_number: `R-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}-${Math.floor(Math.random() * 9000) + 1000}`,
      shift_id: shiftId,
      customer_id: ticket.customerId,
      cashier_id: cashier.uid,
      total_amount: total,
      points_earned: pointsEarned,
      payment_method: { method: paymentMethod },
      status: 'COMPLETED' as const,
      discount_amount: discountAmount,
      discount_details: ticket.discountType ? { type: ticket.discountType, value: ticket.discountValue } : null
    };

    const { data: newOrderData, error: orderError } = await db.from(ORDER_TABLE).insert(orderPayload).select().single();
    if (orderError) throw orderError;
    const newOrder = newOrderData as any;

    // 2. Create Order Items
    const orderItemsPayload = ticket.items.map(item => ({
      order_id: newOrder.id,
      product_id: item.productId,
      quantity: item.quantity,
      price_per_unit: item.price,
      subtotal: item.price * item.quantity
    }));

    const { error: itemsError } = await db.from(ORDER_ITEM_TABLE).insert(orderItemsPayload);
    if (itemsError) {
      await db.from(ORDER_TABLE).delete().eq('id', newOrder.id); // Rollback
      throw itemsError;
    }

    // 3. Process Loyalty Ledger (if applicable)
    if (ticket.customerId && pointsEarned > 0) {
       await db.rpc('process_safe_transaction', {
        p_customer_id: ticket.customerId,
        p_type: 'EARN',
        p_amount: pointsEarned,
        p_details: { order_id: newOrder.id, order_number: newOrder.order_number }
      });
    }

    // Return the new order object, inventory is processed separately
    return {
        ...newOrder,
        orderNumber: newOrder.order_number,
        totalAmount: newOrder.total_amount,
        customerId: newOrder.customer_id,
        cashierId: newOrder.cashier_id,
        shiftId: newOrder.shift_id,
        discountAmount: newOrder.discount_amount,
        pointsEarned: newOrder.points_earned,
        paymentMethod: newOrder.payment_method,
        createdAt: newOrder.createdAt
      } as Order;
  }

  async getOrder(orderId: string): Promise<Order | null> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return null;
    }

    const { data, error } = await this.supabase
      .from(ORDER_TABLE)
      .select(`
        *,
        order_items ( *, products ( name, unit ) ),
        customers ( full_name )
      `)
      .eq('id', orderId)
      .single();

    if (error) throw error;
    
    // Map snake_case to camelCase
    return {
        id: data.id,
        orderNumber: data.order_number,
        shiftId: data.shift_id,
        customerId: data.customer_id,
        cashierId: data.cashier_id,
        totalAmount: data.total_amount,
        discountAmount: data.discount_amount,
        discountDetails: data.discount_details,
        pointsEarned: data.points_earned,
        paymentMethod: data.payment_method,
        status: data.status,
        createdAt: data.createdAt,
        items: data.order_items.map((oi: any) => ({
            id: oi.id,
            orderId: oi.order_id,
            productId: oi.product_id,
            quantity: oi.quantity,
            pricePerUnit: oi.price_per_unit,
            subtotal: oi.subtotal,
            // Include product details for receipt
            name: oi.products.name,
            unit: oi.products.unit,
        })),
        customers: data.customers,
    } as Order;
  }
  
  async voidOrderAndRestock(orderId: string, itemsToRestock: { productId: string, quantity: number }[]): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) {
      throw new Error("Database not connected.");
    }

    const { error } = await this.supabase.rpc('void_order_and_restock', {
      p_order_id: orderId,
      p_items_to_restock: itemsToRestock
    });

    if (error) throw error;
  }
}